/**
 * CUSTOM_TAXHUB_BY_CLAUDE — Tax Hub, client logic (Phase 1)
 * =========================================================
 * Taxable sales by state. Pick a state + a period, tick the charges that state
 * taxes, get the number and the orders behind it.
 *
 * ── ONE FETCH, EVERYTHING SLICED FROM IT ──────────────────────────────────────
 * We ask for a SUPERSET window — [Jan 1 last year .. today] — not the period on
 * screen. `buildTaxRows` filters by date AFTER the dashboard build, so the 3-6s
 * cost is identical whatever window we name; only the JSON gets longer. Two extra
 * years of rows is a cheap trade for:
 *   - the YTD and last-year counters, at no extra request
 *   - instant switching between every preset, not just state/checkbox changes
 * Only a CUSTOM range reaching outside the superset goes back to the server.
 *
 * That is the shape of the feature, not an optimisation. Taxability differs state
 * by state, and the screen's job is letting someone flip a checkbox and watch the
 * number move. A round-trip per tick would make it unusable.
 *
 * ⛔ Consequence: the server's per-window `meta` counts (unassigned / partial /
 * weakDate / collectedUnknown) now describe the SUPERSET, not what is on screen.
 * Every warning is therefore counted client-side off the visible slice. The one
 * exception is `awaitingCollection`, whose deals have no date at all and so sit
 * outside every window equally — the server's global figure is right for it.
 *
 * ── THE CHECKBOXES ARE REMEMBERED PER STATE ───────────────────────────────────
 * Colorado's answer to "does this state tax freight" is not Georgia's, and
 * whoever sets it once should not set it again next month. Selections persist in
 * localStorage keyed by state code. ⚠ That is a per-browser convenience, NOT a
 * record of a state's tax law — it does not sync, it is not authoritative, and
 * when the hub grows a real per-state taxability table (Phase 2) that table wins
 * and this becomes a seed. Do not let anyone file off it.
 *
 * ── WHAT THE NUMBER MEANS ─────────────────────────────────────────────────────
 * "Sales sourced to this state, collected in this period" — the input to a
 * return, not the return. The period date is when the FUNDS LANDED (the last
 * payment on the deal), so a deal collected in instalments sits entirely in the
 * period of its final one. Those rows are flagged "part paid" rather than split:
 * splitting a sale across filing periods is a policy call, not a display choice.
 *
 * It knows nothing about exemption certificates or nexus, and an offline deal
 * refunded after the sale still shows its full amount. The footnote on the page
 * says so; keep it there until those are real.
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://rfq-worker.ryan-29b.workers.dev';
  var LOGIN_PAGE = '/pages/rep-quote-portal';
  var PREFS_PREFIX = 'taxhub_components_';
  var PREFS_PERIOD = 'taxhub_period';
  var PREFS_RANK = 'taxhub_rank_collapsed';

  /* Component keys MUST match TAX_COMPONENTS in rfq-worker's
     src/handlers/tax-hub.js — they are the field names on every order row.
     The server sends its own labels; these are the fallback if it ever doesn't. */
  var COMPONENTS = [
    { key: 'product',  label: 'Product' },
    { key: 'shipping', label: 'Shipping' },
    { key: 'install',  label: 'Installation' },
    { key: 'cardFee',  label: 'Credit card fees' },
    { key: 'taxes',    label: 'Tax collected' }
  ];

  var ALL_STATES = '__ALL__';
  var UNASSIGNED = '__NONE__';

  var state = {
    loading: false,
    error: null,
    data: null,          // last successful /tax-hub/sales payload
    periodKey: 'last_month',
    from: '',
    to: '',
    stateCode: '',       // '' until the first load picks one
    checked: {}          // component key -> bool, for the current state
  };

  // ------------------------------------------------------------------
  // Session + API
  // ------------------------------------------------------------------
  function getSession() {
    if (window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession) {
      return window.RepPortal.Auth.getSession();
    }
    try {
      var raw = sessionStorage.getItem('rep_portal_session') || localStorage.getItem('rep_portal_session');
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  /* Routed through RepPortal.API so the access token refreshes and a 401 retries
     once. A raw fetch here would sign the user out the first time a token aged
     out mid-report. */
  function api(path) {
    if (!(window.RepPortal && window.RepPortal.API && window.RepPortal.API.fetchExternal)) {
      return Promise.reject(new Error('Portal API helper unavailable. Reload the page.'));
    }
    return window.RepPortal.API.fetchExternal(WORKER_URL, path, { method: 'GET' });
  }

  // ------------------------------------------------------------------
  // Dates — periods are calendar-based, in the viewer's local time.
  // Sales tax is filed on calendar periods, so "last quarter" means the
  // calendar quarter, never a rolling 90 days.
  // ------------------------------------------------------------------
  function ymd(d) {
    var m = String(d.getMonth() + 1); if (m.length < 2) m = '0' + m;
    var day = String(d.getDate()); if (day.length < 2) day = '0' + day;
    return d.getFullYear() + '-' + m + '-' + day;
  }

  var PERIODS = [
    { key: 'this_month',   label: 'This month' },
    { key: 'last_month',   label: 'Last month' },
    { key: 'this_quarter', label: 'This quarter' },
    { key: 'last_quarter', label: 'Last quarter' },
    { key: 'last_6',       label: 'Last 6 months' },
    { key: 'ytd',          label: 'Year to date' },
    { key: 'last_year',    label: 'Last year' },
    { key: 'custom',       label: 'Custom' }
  ];

  function rangeFor(key) {
    var now = new Date();
    var y = now.getFullYear();
    var m = now.getMonth();
    var q = Math.floor(m / 3);
    var start, end;
    switch (key) {
      case 'this_month':   start = new Date(y, m, 1);         end = new Date(y, m + 1, 0); break;
      case 'last_month':   start = new Date(y, m - 1, 1);     end = new Date(y, m, 0); break;
      case 'this_quarter': start = new Date(y, q * 3, 1);     end = new Date(y, q * 3 + 3, 0); break;
      case 'last_quarter': start = new Date(y, q * 3 - 3, 1); end = new Date(y, q * 3, 0); break;
      // Six whole calendar months ending with the last complete one.
      case 'last_6':       start = new Date(y, m - 6, 1);     end = new Date(y, m, 0); break;
      case 'ytd':          start = new Date(y, 0, 1);         end = now; break;
      case 'last_year':    start = new Date(y - 1, 0, 1);     end = new Date(y - 1, 11, 31); break;
      default: return null;
    }
    return { from: ymd(start), to: ymd(end) };
  }

  // ------------------------------------------------------------------
  // Prefs
  // ------------------------------------------------------------------
  function loadChecked(stateCode) {
    var fallback = { product: true, shipping: false, install: false, cardFee: false, taxes: false };
    try {
      var raw = localStorage.getItem(PREFS_PREFIX + stateCode);
      if (!raw) return fallback;
      var parsed = JSON.parse(raw);
      var out = {};
      for (var i = 0; i < COMPONENTS.length; i++) {
        var k = COMPONENTS[i].key;
        out[k] = parsed[k] === true;
      }
      return out;
    } catch (e) { return fallback; }
  }

  function saveChecked(stateCode, checked) {
    try { localStorage.setItem(PREFS_PREFIX + stateCode, JSON.stringify(checked)); } catch (e) {}
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function usd(n) {
    var v = Number(n) || 0;
    return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function prettyDate(s) {
    var parts = String(s || '').split('-');
    if (parts.length !== 3) return s || '';
    var d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function componentList() {
    var fromServer = state.data && state.data.components;
    return (fromServer && fromServer.length) ? fromServer : COMPONENTS;
  }

  /**
   * The window we actually ASK the server for: always at least
   * [Jan 1 last year .. today], widened if a custom range reaches outside it.
   *
   * ⛔ This is deliberately wider than what is on screen. buildTaxRows filters by
   * date AFTER the dashboard build, so the build — the whole 3-6s cost — is
   * identical whatever window we name; only the JSON gets longer. Buying two
   * extra years of rows costs almost nothing and buys the YTD / last-year
   * counters, plus instant switching between every preset, with no second
   * request. Narrowing this to the selected period would make each counter its
   * own round-trip for no saving.
   */
  function supersetWindow() {
    var now = new Date();
    return { from: ymd(new Date(now.getFullYear() - 1, 0, 1)), to: ymd(now) };
  }

  function fetchWindow() {
    var sup = supersetWindow();
    return {
      from: (state.from && state.from < sup.from) ? state.from : sup.from,
      to: (state.to && state.to > sup.to) ? state.to : sup.to
    };
  }

  /**
   * Slice the held dataset. Every figure on the page comes through here, so the
   * selected period, the YTD counter and the last-year counter are all the same
   * arithmetic over the same rows — they cannot disagree.
   * Unassigned is a real selection, not a bug.
   */
  function ordersIn(stateCode, from, to) {
    if (!state.data) return [];
    var all = state.data.orders || [];
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var o = all[i];
      var d = o.collectedDate || '';
      if (from && d < from) continue;
      if (to && d > to) continue;
      if (stateCode === UNASSIGNED) { if (o.state) continue; }
      else if (stateCode !== ALL_STATES && o.state !== stateCode) continue;
      out.push(o);
    }
    return out;
  }

  function selectedOrders() {
    /* CUSTOM_TAXFILING_BY_CLAUDE (KAREN 5ba17d6bcbae) - a state + period marked FILED shows the rows as they
       were frozen, never today's: a late edit to a deal must not quietly change a number we reported. */
    var fz = frozenNow();
    if (fz && fz.rows) return fz.rows;
    return ordersIn(state.stateCode, state.from, state.to);
  }

  /* CUSTOM_TAXFILING_BY_CLAUDE - the filing card (custom-tax-filing.js) owns the filed list and the snapshots.
     null = this state + period was never marked filed (or is unlocked); {loading} while the snapshot is read. */
  function frozenNow() {
    var F = window.TaxFiling;
    if (!F || typeof F.frozenFor !== 'function') return null;
    if (state.stateCode === ALL_STATES || state.stateCode === UNASSIGNED) return null;
    return F.frozenFor(state.stateCode, state.from, state.to);
  }

  function frozenBannerHtml() {
    var fz = frozenNow();
    if (!fz) return '';
    if (fz.loading) return '<div class="th-frozen"><p class="th-frozen__lead">This period was marked filed. Loading the numbers as they were filed&hellip;</p></div>';
    if (fz.error) return '<div class="th-frozen th-frozen--err"><p class="th-frozen__lead">This period was marked filed, but its frozen numbers could not be read (' + esc(fz.error) + '). The figures below are LIVE.</p></div>';
    var rec = fz.rec || {};
    var live = ordersIn(state.stateCode, state.from, state.to);
    var frozenTotal = 0, liveTotal = 0, i;
    for (i = 0; i < fz.rows.length; i++) frozenTotal += rowSubtotal(fz.rows[i]);
    for (i = 0; i < live.length; i++) liveTotal += rowSubtotal(live[i]);
    var byRef = {};
    for (i = 0; i < fz.rows.length; i++) byRef[fz.rows[i].ref] = fz.rows[i];
    var moved = 0, seen = {};
    for (i = 0; i < live.length; i++) {
      var o = live[i]; seen[o.ref] = true;
      if (!byRef[o.ref] || Math.abs((Number(byRef[o.ref].grandTotal) || 0) - (Number(o.grandTotal) || 0)) > 0.005) moved++;
    }
    for (var ref in byRef) if (!seen[ref]) moved++;
    var h = '<div class="th-frozen">';
    h += '<p class="th-frozen__title">Filed ' + esc(prettyDate(String(rec.filedAt || '').slice(0, 10))) + (rec.filedBy ? ' by ' + esc(rec.filedBy) : '')
       + (rec.amountPaid != null ? ' &middot; ' + usd(rec.amountPaid) + ' paid' : '') + '</p>';
    h += '<p class="th-frozen__lead">These are the numbers as they were filed. They are frozen, so later changes to a deal do not move them.</p>';
    if (moved) {
      h += '<p class="th-frozen__drift">Since filing, ' + moved + ' deal' + (moved === 1 ? '' : 's') + ' in this period changed. Live total today: '
         + (anyChecked() ? usd(Math.round(liveTotal * 100) / 100) : '&mdash;') + ' across ' + live.length + ' order' + (live.length === 1 ? '' : 's')
         + (anyChecked() ? ' (filed: ' + usd(Math.round(frozenTotal * 100) / 100) + ')' : '') + '. If this needs an amended return, Ryan can unlock the period on the Filing calendar card.</p>';
    } else {
      h += '<p class="th-frozen__ok">Nothing has changed since filing.</p>';
    }
    h += '</div>';
    return h;
  }

  /** Sum the ticked components across a set of rows. */
  function sumChecked(orders) {
    var comps = componentList();
    var total = 0;
    for (var i = 0; i < orders.length; i++) total += rowSubtotal(orders[i]);
    return Math.round(total * 100) / 100;
    // (rowSubtotal already honours state.checked; comps kept for symmetry.)
  }

  /**
   * Every state that saw money in a window, biggest first, on the ticked
   * components. The unassigned bucket rides along as a normal entry — it is
   * money we sold, and hiding it from the ranking is how a pile of it stays
   * invisible.
   *
   * ⛔ Ordered by TOTAL, except when nothing is ticked: then every total is $0
   * and sorting by it would produce an arbitrary order that still looks ranked.
   * In that case it falls back to order count, which is a real ordering.
   */
  function rankedStates(from, to) {
    var buckets = {};
    var all = (state.data && state.data.orders) || [];
    for (var i = 0; i < all.length; i++) {
      var o = all[i];
      var d = o.collectedDate || '';
      if (from && d < from) continue;
      if (to && d > to) continue;
      var k = o.state || '';
      if (!buckets[k]) buckets[k] = { code: k, orders: 0, total: 0, gross: 0 };
      buckets[k].orders += 1;
      buckets[k].total += rowSubtotal(o);
      buckets[k].gross += Number(o.grandTotal) || 0;
    }

    var out = [];
    for (var code in buckets) {
      var b = buckets[code];
      b.total = Math.round(b.total * 100) / 100;
      b.gross = Math.round(b.gross * 100) / 100;
      b.name = code ? stateNameOf(code) : 'No state on the address';
      out.push(b);
    }

    var ticked = anyChecked();
    out.sort(function (a, b2) {
      if (ticked && b2.total !== a.total) return b2.total - a.total;
      if (b2.orders !== a.orders) return b2.orders - a.orders;
      return a.name < b2.name ? -1 : a.name > b2.name ? 1 : 0;
    });
    return out;
  }

  function stateNameOf(code) {
    var list = (state.data && state.data.stateList) || [];
    for (var i = 0; i < list.length; i++) if (list[i].code === code) return list[i].name;
    return code;
  }

  /** Order counts per state code for a window — drives the picker labels. */
  function countsFor(from, to) {
    var map = {};
    var all = (state.data && state.data.orders) || [];
    for (var i = 0; i < all.length; i++) {
      var o = all[i];
      var d = o.collectedDate || '';
      if (from && d < from) continue;
      if (to && d > to) continue;
      var k = o.state || '';
      map[k] = (map[k] || 0) + 1;
    }
    return map;
  }

  function rowSubtotal(o) {
    var sum = 0;
    var comps = componentList();
    for (var i = 0; i < comps.length; i++) {
      if (state.checked[comps[i].key]) sum += Number(o[comps[i].key]) || 0;
    }
    return Math.round(sum * 100) / 100;
  }

  function anyChecked() {
    var comps = componentList();
    for (var i = 0; i < comps.length; i++) if (state.checked[comps[i].key]) return true;
    return false;
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  function root() { return document.getElementById('tax-hub-root'); }

  function render() {
    var el = root();
    if (!el) return;
    el.innerHTML = controlsHtml() + resultHtml();
    bindControls();
  }

  function controlsHtml() {
    var h = '';
    h += '<div class="th-controls">';

    // Period. The dates stay VISIBLE on a preset (disabled, showing what the
    // preset resolved to) rather than being hidden — "last quarter" should say
    // which dates that is, not make you trust it.
    var isCustom = state.periodKey === 'custom';
    h += '<div class="th-field th-field--period">';
    h += '<label class="th-field__label" for="thPeriod">Period</label>';
    h += '<div class="th-field__row">';
    h += '<select class="th-select th-select--period" id="thPeriod">';
    for (var i = 0; i < PERIODS.length; i++) {
      h += '<option value="' + PERIODS[i].key + '"' + (PERIODS[i].key === state.periodKey ? ' selected' : '') + '>'
         + esc(PERIODS[i].label) + '</option>';
    }
    h += '</select>';
    h += '<div class="th-dates">';
    h += '<input type="date" class="th-date" id="thFrom" value="' + esc(state.from) + '"'
       + (isCustom ? '' : ' disabled') + ' aria-label="From date">';
    h += '<span class="th-dates__sep">to</span>';
    h += '<input type="date" class="th-date" id="thTo" value="' + esc(state.to) + '"'
       + (isCustom ? '' : ' disabled') + ' aria-label="To date">';
    h += '</div>';
    if (isCustom) {
      h += '<button type="button" class="th-btn th-btn--primary" id="thRun">Run</button>';
    }
    h += '</div></div>';

    // State
    h += '<div class="th-field th-field--state">';
    h += '<label class="th-field__label" for="thState">State</label>';
    h += '<select class="th-select" id="thState">' + stateOptionsHtml() + '</select>';
    h += '</div>';

    // Components
    h += '<div class="th-field th-field--components">';
    h += '<span class="th-field__label">Taxable in this state</span>';
    h += '<div class="th-checks">';
    var comps = componentList();
    for (var c = 0; c < comps.length; c++) {
      var k = comps[c].key;
      h += '<label class="th-check' + (state.checked[k] ? ' th-check--on' : '') + '">'
         + '<input type="checkbox" data-comp="' + esc(k) + '"' + (state.checked[k] ? ' checked' : '') + '>'
         + '<span>' + esc(comps[c].label) + '</span></label>';
    }
    h += '</div></div>';

    h += '</div>';
    return h;
  }

  function stateOptionsHtml() {
    if (!state.data) return '<option>Loading…</option>';
    /* Counted from the rows in the SELECTED period, not from the server's rollup —
       that rollup now covers the whole two-year superset we fetch, so using it
       would label every state with a count the screen does not show. */
    var counts = countsFor(state.from, state.to);

    var h = '';
    var allCount = ordersIn(ALL_STATES, state.from, state.to).length;
    h += '<option value="' + ALL_STATES + '"' + (state.stateCode === ALL_STATES ? ' selected' : '') + '>'
       + 'All states (' + allCount + ')</option>';

    var stateList = state.data.stateList || [];
    var withSales = [];
    var without = [];
    for (var s = 0; s < stateList.length; s++) {
      var st = stateList[s];
      var n = counts[st.code] || 0;
      var label = st.name + ' (' + st.code + ')' + (n ? ' — ' + n + ' order' + (n === 1 ? '' : 's') : '');
      var opt = '<option value="' + st.code + '"' + (state.stateCode === st.code ? ' selected' : '') + '>' + esc(label) + '</option>';
      (n ? withSales : without).push(opt);
    }
    // States with activity first — the picker should lead with where the money is,
    // but all 51 stay selectable so a zero is a readable answer, not a missing row.
    if (withSales.length) h += '<optgroup label="Sales this period">' + withSales.join('') + '</optgroup>';
    if (without.length) h += '<optgroup label="No sales this period">' + without.join('') + '</optgroup>';

    if (counts['']) {
      h += '<optgroup label="Needs attention">'
         + '<option value="' + UNASSIGNED + '"' + (state.stateCode === UNASSIGNED ? ' selected' : '') + '>'
         + 'No state on the address (' + counts[''] + ')</option></optgroup>';
    }
    return h;
  }

  function resultHtml() {
    if (state.loading) {
      return '<div class="th-notice"><p class="th-notice__lead">Building the report&hellip; '
           + 'this walks every deal in the window and takes a few seconds.</p></div>';
    }
    if (state.error) {
      return '<div class="th-notice th-notice--error">'
           + '<p class="th-notice__title">That didn&rsquo;t load</p>'
           + '<p class="th-notice__lead">' + esc(state.error) + '</p>'
           + '<button type="button" class="th-btn th-btn--primary" id="thRetry">Try again</button>'
           + '</div>';
    }
    if (!state.data) return '';

    var orders = selectedOrders();
    var comps = componentList();

    // Per-component subtotals across the selected orders (all components, so an
    // unticked one still shows what ticking it would add).
    var subtotals = {};
    for (var i = 0; i < comps.length; i++) subtotals[comps[i].key] = 0;
    var grand = 0;
    for (var o = 0; o < orders.length; o++) {
      for (var j = 0; j < comps.length; j++) {
        subtotals[comps[j].key] += Number(orders[o][comps[j].key]) || 0;
      }
      grand += Number(orders[o].grandTotal) || 0;
    }
    var total = 0;
    for (var k = 0; k < comps.length; k++) if (state.checked[comps[k].key]) total += subtotals[comps[k].key];

    var h = '';

    h += frozenBannerHtml(); // CUSTOM_TAXFILING_BY_CLAUDE

    // Headline
    h += '<div class="th-summary">';
    h += '<div class="th-summary__main">';
    h += '<span class="th-summary__label">' + esc(headlineLabel()) + '</span>';
    h += '<span class="th-summary__value">' + (anyChecked() ? usd(total) : '&mdash;') + '</span>';
    h += '<span class="th-summary__meta">'
       + orders.length + ' order' + (orders.length === 1 ? '' : 's') + ' &middot; '
       + esc(prettyDate(state.data.from)) + ' to ' + esc(prettyDate(state.data.to))
       + ' &middot; ' + usd(grand) + ' charged in total'
       + '</span>';
    if (!anyChecked()) {
      h += '<span class="th-summary__hint">Tick at least one charge above to get a figure.</span>';
    }
    h += '</div>';
    h += '<div class="th-summary__breakdown">';
    for (var b = 0; b < comps.length; b++) {
      var ck = comps[b].key;
      h += '<div class="th-chip' + (state.checked[ck] ? ' th-chip--on' : '') + '">'
         + '<span class="th-chip__label">' + esc(comps[b].label) + '</span>'
         + '<span class="th-chip__value">' + usd(subtotals[ck]) + '</span></div>';
    }
    h += '</div>';
    h += '</div>';

    // Year counters for the selected state
    h += counterHtml();

    /* Cross-state ranking for the selected period. Sits above the per-state
       warnings and order table because it answers "which state" — the question
       that comes before "how much into this one". */
    h += rankHtml();

    /* Warnings, counted from the ROWS ON SCREEN — not from meta.
       ⛔ The server's per-window counts now describe the SUPERSET we fetched
       (two years), not the period being viewed, so reading them here would
       over-report every warning. awaitingCollection is the one exception: those
       deals have no date at all, so they sit outside every window equally and
       the server's global figure is the right one. */
    var meta = state.data.meta || {};
    var nUnassigned = ordersIn(UNASSIGNED, state.from, state.to).length;
    var nPartial = 0, nUnknown = 0, nWeak = 0;
    for (var w = 0; w < orders.length; w++) {
      if (orders[w].partial) nPartial++;
      if (orders[w].collectedKnown === false) nUnknown++;
      var b = orders[w].collectedBasis;
      if (b && b !== 'payment' && b !== 'online') nWeak++;
    }

    if (nUnassigned > 0 && state.stateCode !== UNASSIGNED) {
      h += '<p class="th-warn">' + nUnassigned + ' sold order'
         + (nUnassigned === 1 ? '' : 's') + ' in this period had no usable state on the address, '
         + 'so ' + (nUnassigned === 1 ? 'it is' : 'they are') + ' in no state total. '
         + 'Pick &ldquo;No state on the address&rdquo; to see ' + (nUnassigned === 1 ? 'it' : 'them') + '.</p>';
    }
    /* ⛔ Coverage first, above every other warning. If part of the source data
       never arrived, a small total is not a finding about sales — it is a broken
       report, and the two are indistinguishable without this. This is the class of
       failure that had the hub reporting 103 YTD against a true 444. */
    var cov = meta.coverage || {};
    if (cov.onlineError) {
      h += '<p class="th-warn th-warn--stop"><strong>Shopify orders did not load.</strong> '
         + 'This report is showing offline deals only, so every figure on this page is '
         + 'incomplete &mdash; do not file from it. (' + esc(String(cov.onlineError)) + ')</p>';
    }
    if (cov.onlineTruncated) {
      h += '<p class="th-warn th-warn--stop"><strong>The Shopify fetch hit its page cap.</strong> '
         + 'Older orders in this window are missing. Narrow the period or raise '
         + 'TAX_ONLINE_MAX_PAGES.</p>';
    }
    if (cov.offlineTruncated) {
      h += '<p class="th-warn th-warn--stop"><strong>Offline deals were paginated.</strong> '
         + 'Only ' + (cov.offlineRowsSeen || 0) + ' of ' + (cov.offlineChains || 0)
         + ' reached this report &mdash; the totals are short.</p>';
    }
    if (cov.onlineDuplicate > 0) {
      h += '<p class="th-warn th-warn--stop"><strong>' + cov.onlineDuplicate + ' order'
         + (cov.onlineDuplicate === 1 ? ' was' : 's were') + ' counted twice and removed.</strong> '
         + 'The online and offline halves have started overlapping &mdash; investigate before '
         + 'filing.</p>';
    }

    var awaiting = meta.awaitingCollection || {};
    if (awaiting.count > 0) {
      h += '<p class="th-warn">' + awaiting.count + ' sold deal'
         + (awaiting.count === 1 ? '' : 's') + ' worth ' + usd(awaiting.total)
         + ' ha' + (awaiting.count === 1 ? 's' : 've') + ' no payment recorded at all, so '
         + (awaiting.count === 1 ? 'it is' : 'they are')
         + ' in no period &mdash; not this one and not any other.</p>';
    }
    if (nPartial > 0) {
      h += '<p class="th-warn">' + nPartial + ' order'
         + (nPartial === 1 ? ' here is' : 's here are') + ' only part paid. The full sale is '
         + 'counted in the period of its last payment, so this total runs ahead of what has '
         + 'actually been collected.</p>';
    }
    if (nUnknown > 0) {
      h += '<p class="th-warn">' + nUnknown + ' order'
         + (nUnknown === 1 ? '' : 's') + ' here (marked <strong>amount n/r</strong>) '
         + 'ha' + (nUnknown === 1 ? 's' : 've') + ' no collected amount recorded. '
         + 'The sale total is still counted &mdash; only the amount-received check is blind for '
         + (nUnknown === 1 ? 'it' : 'them') + '.</p>';
    }
    if (nWeak > 0) {
      h += '<p class="th-warn">' + nWeak + ' order'
         + (nWeak === 1 ? '' : 's') + ' here (marked <strong>est</strong>) had no dated '
         + 'payment record, so ' + (nWeak === 1 ? 'its date was' : 'their dates were')
         + ' taken from the deal instead. Check ' + (nWeak === 1 ? 'it' : 'them')
         + ' before filing on a tight period boundary.</p>';
    }

    // Orders
    h += '<div class="th-table-head">';
    h += '<h2 class="th-table-title">Orders</h2>';
    if (orders.length) h += '<button type="button" class="th-btn" id="thCsv">Download CSV</button>';
    h += '</div>';

    if (!orders.length) {
      h += '<div class="th-notice"><p class="th-notice__lead">'
         + 'No sold orders sourced to ' + esc(headlineWhere()) + ' in this period.'
         + '</p></div>';
    } else {
      h += '<div class="th-table-wrap"><table class="th-table"><thead><tr>';
      h += '<th>Collected</th><th>Reference</th><th>Customer</th><th>Ship to</th><th>Source</th>';
      for (var th = 0; th < comps.length; th++) {
        h += '<th class="th-num' + (state.checked[comps[th].key] ? ' th-num--on' : '') + '">'
           + esc(comps[th].label) + '</th>';
      }
      h += '<th class="th-num th-num--total">Selected</th></tr></thead><tbody>';
      for (var r = 0; r < orders.length; r++) {
        var row = orders[r];
        var where = [row.city, row.state, row.zip].filter(Boolean).join(' ');
        h += '<tr>';
        h += '<td>' + esc(prettyDate(row.collectedDate));
        /* A date that did not come from a real payment record is a weaker answer than
           one that did. Say so, rather than letting the column imply otherwise. */
        if (row.collectedBasis && row.collectedBasis !== 'payment') {
          h += ' <span class="th-flag" title="No dated payment record on this deal — dated from '
             + (row.collectedBasis === 'scalar' ? 'the payment summary' : 'the sold date')
             + '">est</span>';
        }
        h += '</td>';
        h += '<td class="th-ref">' + esc(row.ref);
        /* Collected in instalments and still short. The whole sale sits in the period
           of its LAST payment, so this row is front-loading revenue. */
        if (row.partial) {
          h += ' <span class="th-flag th-flag--warn" title="' + usd(row.collected)
             + ' collected of ' + usd(row.grandTotal) + '">part paid</span>';
        } else if (row.collectedKnown === false) {
          /* Nobody recorded HOW MUCH was collected. That is an absence, not a
             shortfall — saying "part paid" here would assert a gap we cannot see. */
          h += ' <span class="th-flag" title="No collected amount recorded on this deal">'
             + 'amount n/r</span>';
        }
        h += '</td>';
        h += '<td>' + esc(row.company || row.customer) + '</td>';
        h += '<td>' + esc(where || '—');
        // Say when the state came from the billing address instead of a ship-to.
        // Destination sourcing is the rule; a bill-to answer is a judgement call
        // someone may need to overrule, so it is labelled rather than hidden.
        if (row.stateBasis === 'bill') h += ' <span class="th-flag" title="No ship-to address on this order — sourced to the billing state">billing</span>';
        if (row.stateBasis === 'none') h += ' <span class="th-flag th-flag--warn" title="No usable state on this order">no state</span>';
        h += '</td>';
        h += '<td><span class="th-src th-src--' + esc(row.source) + '">' + esc(row.source) + '</span></td>';
        for (var cc = 0; cc < comps.length; cc++) {
          var key = comps[cc].key;
          h += '<td class="th-num' + (state.checked[key] ? ' th-num--on' : '') + '">' + usd(row[key]) + '</td>';
        }
        h += '<td class="th-num th-num--total">' + usd(rowSubtotal(row)) + '</td>';
        h += '</tr>';
      }
      h += '</tbody></table></div>';
    }

    h += '<p class="th-foot">'
       + 'Sales sourced to a state by ship-to address (billing when there is no ship-to), dated by '
       + 'when the funds were collected &mdash; the last payment on the deal, so an instalment sale '
       + 'sits entirely in the period of its final payment. &ldquo;Credit card fees&rdquo; is the '
       + '2.9% surcharged to the customer, not the ~2.7% + 30&cent; the processor charges us; that '
       + 'line currently goes out non-taxable, so ticking it reports what a state would tax if it '
       + 'required it, not what we charged. This is the input to a return, not the return: it does '
       + 'not know about exemption certificates or nexus, and an offline deal refunded after the '
       + 'sale still shows its full amount.'
       + '</p>';

    return h;
  }

  /**
   * Year counters for the SELECTED state: this year so far, and last year whole.
   *
   * They use the same ticked components as the headline, so the three figures are
   * directly comparable — a YTD built on a different basis than the number above
   * it would invite exactly the comparison it cannot support.
   *
   * Both windows sit inside the superset we already hold, so these cost nothing
   * and stay correct as the state or the checkboxes change.
   */
  function counterHtml() {
    var now = new Date();
    var thisYear = now.getFullYear();
    var ytdR = rangeFor('ytd');
    var lyR = rangeFor('last_year');

    var cards = [
      { label: 'Year to date', sub: String(thisYear), range: ytdR,
        active: state.periodKey === 'ytd' },
      { label: 'Last year', sub: String(thisYear - 1), range: lyR,
        active: state.periodKey === 'last_year' }
    ];

    var h = '<div class="th-counters" aria-label="Year totals for ' + esc(headlineWhere()) + '">';
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var rows = ordersIn(state.stateCode, c.range.from, c.range.to);
      var total = sumChecked(rows);
      h += '<div class="th-counter' + (c.active ? ' th-counter--active' : '') + '">';
      h += '<span class="th-counter__label">' + esc(c.label)
         + ' <span class="th-counter__year">' + esc(c.sub) + '</span></span>';
      h += '<span class="th-counter__value">' + (anyChecked() ? usd(total) : '&mdash;') + '</span>';
      h += '<span class="th-counter__meta">' + rows.length + ' order'
         + (rows.length === 1 ? '' : 's') + ' &middot; ' + esc(headlineWhere()) + '</span>';
      if (c.active) h += '<span class="th-counter__flag">shown above</span>';
      h += '</div>';
    }
    h += '</div>';
    return h;
  }

  /**
   * Sales by state for the selected period, biggest first.
   *
   * The cross-state view the rest of the page does not give: everything else
   * answers "how much into THIS state", this answers "which states, and in what
   * order". Rows are clickable and select that state, so it doubles as the way
   * into the drill-down.
   *
   * Every state with activity is listed — no top-N truncation. A ranked list
   * that quietly stops at ten reads as "these are all of them".
   */
  function rankCollapsed() {
    try { return localStorage.getItem(PREFS_RANK) === '1'; } catch (e) { return false; }
  }

  /**
   * Collapse toggle for the Sales-by-state card.
   *
   * Mirrors the Deal Dashboard's collapsible workflow cards — `dashboard.css`
   * ~L6654 (`.rqd-detail__arap--collapsible`) and `dashboard-main.js` ~L2617 —
   * down to the timings: a 0.4s 360° chevron spin with the glyph swapped at the
   * 200ms midpoint, and a 0.3s max-height slide on the body.
   *
   * ⚠ Same behaviour, OWN class names, because `dashboard.css` is not loaded on
   * this page — only `custom-rep-portal.css` + this tool's sheet. Reusing the
   * `rqd-` classes would style nothing. If a third surface ever wants this, it
   * should be promoted into `custom-rep-portal.css` rather than copied again.
   *
   * ⛔ Toggles the class in place instead of re-rendering. A re-render would
   * rebuild the node and the CSS transition would never run — the card would
   * snap rather than slide.
   */
  function bindRankCollapse() {
    var card = document.querySelector('.th-rank--collapsible');
    if (!card) return;
    var head = card.querySelector('.th-rank__head');
    if (!head) return;

    function toggle() {
      var nowCollapsed = card.classList.toggle('th-rank--collapsed');
      try { localStorage.setItem(PREFS_RANK, nowCollapsed ? '1' : '0'); } catch (e) {}
      head.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');

      var chev = card.querySelector('.th-rank__chevron');
      if (!chev) return;
      chev.classList.remove('th-rank__chevron--spin');
      // Force a reflow so re-adding the class restarts the animation.
      void chev.offsetWidth;
      chev.classList.add('th-rank__chevron--spin');
      setTimeout(function () { chev.textContent = nowCollapsed ? '+' : '−'; }, 200);
      setTimeout(function () { chev.classList.remove('th-rank__chevron--spin'); }, 400);
    }

    head.addEventListener('click', function (e) {
      // The CSV button lives in the header; a click on it is an export, not a fold.
      if (e.target.closest('button, a, input, select, textarea, label')) return;
      toggle();
    });
    head.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); toggle(); }
    });
  }

  function rankHtml() {
    var rows = rankedStates(state.from, state.to);
    var ticked = anyChecked();
    var h = '';

    /* Running totals first — the collapsed header shows them, so they have to
       exist before the head is built. */
    var grand = 0, grandOrders = 0;
    for (var g = 0; g < rows.length; g++) { grand += rows[g].total; grandOrders += rows[g].orders; }
    grand = Math.round(grand * 100) / 100;
    var top = rows.length ? rows[0].total : 0;

    var collapsed = rankCollapsed();

    h = '<div class="th-rank th-rank--collapsible' + (collapsed ? ' th-rank--collapsed' : '') + '">';
    h += '<div class="th-rank__head" role="button" tabindex="0" aria-expanded="'
       + (collapsed ? 'false' : 'true') + '" aria-controls="thRankBody">';
    h += '<h2 class="th-rank__title">Sales by state <span class="th-rank__sub">'
       + esc(prettyDate(state.from)) + ' to ' + esc(prettyDate(state.to)) + '</span></h2>';
    /* Shown only while collapsed (CSS), so folding the card away still leaves the
       headline figure on screen rather than a bare title bar. */
    h += '<span class="th-rank__peek">' + rows.length + ' state' + (rows.length === 1 ? '' : 's')
       + ' &middot; ' + (ticked ? usd(grand) : grandOrders + ' order' + (grandOrders === 1 ? '' : 's'))
       + '</span>';
    if (rows.length) h += '<button type="button" class="th-btn th-btn--sm" id="thRankCsv">Download CSV</button>';
    h += '<span class="th-rank__chevron" aria-hidden="true">' + (collapsed ? '+' : '−') + '</span>';
    h += '</div>';

    if (!rows.length) {
      h += '<div class="th-notice" id="thRankBody"><p class="th-notice__lead">'
         + 'No sold orders in this period, in any state.</p></div></div>';
      return h;
    }

    if (!ticked) {
      h += '<p class="th-rank__note">Nothing is ticked, so there is no figure to rank by &mdash; '
         + 'ordered by order count instead.</p>';
    }

    h += '<div class="th-rank__list" id="thRankBody" role="table">';
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var isSel = (r.code === state.stateCode)
               || (!r.code && state.stateCode === UNASSIGNED);
      // Bar is relative to the LEADER, not to the grand total — it is a
      // comparison between states, which is what a ranking is for.
      var pct = (ticked && top > 0) ? Math.max(1, Math.round((r.total / top) * 100)) : 0;
      var share = (ticked && grand > 0) ? Math.round((r.total / grand) * 1000) / 10 : null;

      h += '<button type="button" class="th-rank__row' + (isSel ? ' th-rank__row--on' : '')
         + (r.code ? '' : ' th-rank__row--none') + '" data-rank-state="' + esc(r.code || UNASSIGNED) + '">';
      h += '<span class="th-rank__pos">' + (i + 1) + '</span>';
      h += '<span class="th-rank__name">' + esc(r.name)
         + (r.code ? ' <span class="th-rank__code">' + esc(r.code) + '</span>' : '') + '</span>';
      h += '<span class="th-rank__bar" aria-hidden="true"><span style="width:' + pct + '%"></span></span>';
      h += '<span class="th-rank__orders">' + r.orders + '</span>';
      h += '<span class="th-rank__share">' + (share === null ? '' : share + '%') + '</span>';
      h += '<span class="th-rank__total">' + (ticked ? usd(r.total) : '&mdash;') + '</span>';
      h += '</button>';
    }
    h += '</div>';

    h += '<div class="th-rank__foot">'
       + '<span>' + rows.length + ' state' + (rows.length === 1 ? '' : 's')
       + ' &middot; ' + grandOrders + ' order' + (grandOrders === 1 ? '' : 's') + '</span>'
       + '<span class="th-rank__grand">' + (ticked ? usd(grand) : '&mdash;') + '</span>'
       + '</div>';

    h += '</div>';
    return h;
  }

  function downloadRankCsv() {
    var rows = rankedStates(state.from, state.to);
    var comps = componentList();
    var head = ['Rank', 'State', 'Code', 'Orders', 'Selected total', 'Order total'];
    var lines = [head.map(csvCell).join(',')];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      lines.push([i + 1, r.name, r.code, r.orders, r.total, r.gross].map(csvCell).join(','));
    }
    var name = 'tax-hub-by-state-' + state.from + '-to-' + state.to + '.csv';
    saveCsv(name, lines.join('\n'));
  }

  function headlineWhere() {
    if (state.stateCode === ALL_STATES) return 'any state';
    if (state.stateCode === UNASSIGNED) return 'an unreadable address';
    var list = (state.data && state.data.stateList) || [];
    for (var i = 0; i < list.length; i++) if (list[i].code === state.stateCode) return list[i].name;
    return state.stateCode;
  }

  function headlineLabel() {
    if (state.stateCode === ALL_STATES) return 'Selected charges, all states';
    if (state.stateCode === UNASSIGNED) return 'Selected charges, no state on the address';
    return 'Taxable sales into ' + headlineWhere();
  }

  // ------------------------------------------------------------------
  // CSV
  // ------------------------------------------------------------------
  function csvCell(v) {
    var s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }

  function downloadCsv() {
    var orders = selectedOrders();
    var comps = componentList();
    var head = ['Collected', 'Date from', 'First payment', 'Reference', 'Customer',
                'City', 'State', 'ZIP', 'State from', 'Source'];
    for (var i = 0; i < comps.length; i++) head.push(comps[i].label);
    head.push('Selected total', 'Order total', 'Amount collected', 'Fully collected');

    var lines = [head.map(csvCell).join(',')];
    for (var r = 0; r < orders.length; r++) {
      var o = orders[r];
      var cells = [o.collectedDate, o.collectedBasis, o.firstPaymentDate, o.ref,
                   o.company || o.customer, o.city, o.state, o.zip, o.stateBasis, o.source];
      for (var c = 0; c < comps.length; c++) cells.push(o[comps[c].key]);
      cells.push(rowSubtotal(o), o.grandTotal, o.collected,
                 o.collectedKnown === false ? 'not recorded' : (o.partial ? 'no' : 'yes'));
      lines.push(cells.map(csvCell).join(','));
    }

    var where = state.stateCode === ALL_STATES ? 'all-states'
              : state.stateCode === UNASSIGNED ? 'no-state' : state.stateCode;
    /* Named for the period ON SCREEN, not state.data.from/to — those are the
       superset we fetched, which would put the wrong dates on every filename. */
    saveCsv('tax-hub-' + where + '-' + state.from + '-to-' + state.to + '.csv', lines.join('\n'));
  }

  /** Shared save path for both exports. */
  function saveCsv(name, text) {
    var blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  // ------------------------------------------------------------------
  // Events
  // ------------------------------------------------------------------
  function bindControls() {
    var period = document.getElementById('thPeriod');
    if (period) period.addEventListener('change', function () {
      state.periodKey = this.value;
      try { localStorage.setItem(PREFS_PERIOD, state.periodKey); } catch (e) {}
      var r = rangeFor(state.periodKey);
      if (r) { state.from = r.from; state.to = r.to; load(); }
      else { render(); }   // custom: wait for Run
    });

    var run = document.getElementById('thRun');
    if (run) run.addEventListener('click', function () {
      var f = document.getElementById('thFrom');
      var t = document.getElementById('thTo');
      var from = f && f.value, to = t && t.value;
      if (!from || !to) { state.error = 'Pick both a start and an end date.'; render(); return; }
      if (from > to) { state.error = 'The start date is after the end date.'; render(); return; }
      state.from = from; state.to = to;
      load();
    });

    var st = document.getElementById('thState');
    if (st) st.addEventListener('change', function () {
      state.stateCode = this.value;
      state.checked = loadChecked(state.stateCode);
      render();
    });

    var boxes = document.querySelectorAll('.th-check input[type="checkbox"]');
    for (var i = 0; i < boxes.length; i++) {
      boxes[i].addEventListener('change', function () {
        state.checked[this.getAttribute('data-comp')] = this.checked;
        saveChecked(state.stateCode, state.checked);
        render();
      });
    }

    var csv = document.getElementById('thCsv');
    if (csv) csv.addEventListener('click', downloadCsv);

    var rankCsv = document.getElementById('thRankCsv');
    if (rankCsv) rankCsv.addEventListener('click', downloadRankCsv);

    /* Guarded the way RepDrawer.init guards each of its steps: bindControls has
       no error boundary, so a throw here would silently cost every handler bound
       AFTER it — the ranking row clicks and the component checkboxes. Collapse is
       a convenience; the controls are the tool. */
    try { bindRankCollapse(); } catch (e) { console.error('[TaxHub] collapse bind failed:', e); }

    /* A ranking row IS the way into that state's detail — clicking one selects
       it, same as the picker, so the overview and the drill-down are one gesture. */
    var rankRows = document.querySelectorAll('[data-rank-state]');
    for (var r = 0; r < rankRows.length; r++) {
      rankRows[r].addEventListener('click', function () {
        state.stateCode = this.getAttribute('data-rank-state');
        state.checked = loadChecked(state.stateCode);
        render();
      });
    }

    var retry = document.getElementById('thRetry');
    if (retry) retry.addEventListener('click', function () { state.error = null; load(); });
  }

  // ------------------------------------------------------------------
  // Load
  // ------------------------------------------------------------------
  /** True when the held dataset already spans the window we would ask for. */
  function haveWindow() {
    if (!state.data) return false;
    var w = fetchWindow();
    return state.data.from <= w.from && state.data.to >= w.to;
  }

  function load() {
    /* Every preset lives inside the superset, so switching period is normally a
       re-render, not a request. Only a custom range reaching outside what we hold
       goes back to the server. */
    if (haveWindow()) { render(); return; }

    state.loading = true;
    state.error = null;
    render();
    var w = fetchWindow();
    var q = '/tax-hub/sales?from=' + encodeURIComponent(w.from) + '&to=' + encodeURIComponent(w.to);
    api(q).then(function (data) {
      state.loading = false;
      state.data = data;
      /* Default the picker to the state with the most sales IN THE SELECTED
         PERIOD rather than to "All states": the screen is answering a per-state
         question, and an all-states total is not a number anyone files.
         ⛔ Not from data.states — that ranks over the whole superset, which would
         pick a state that may have sold nothing in the period on screen. */
      if (!state.stateCode) {
        var counts = countsFor(state.from, state.to);
        var best = '', bestN = 0;
        for (var code in counts) {
          if (!code) continue;                       // skip the unassigned bucket
          if (counts[code] > bestN) { bestN = counts[code]; best = code; }
        }
        state.stateCode = best || ALL_STATES;
        state.checked = loadChecked(state.stateCode);
      }
      render();
    }).catch(function (err) {
      state.loading = false;
      state.data = state.data || null;
      var msg = (err && err.message) || 'Request failed.';
      /* Distinguish "no data" from "the request died" — an empty report and a
         failed one look identical otherwise, and only one of them is a finding. */
      state.error = /403|admins only/i.test(msg)
        ? 'This report is admin-only and the server refused the request.'
        : msg;
      render();
    });
  }

  // ------------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------------
  var booted = false;
  function boot() {
    if (booted) return;
    var session = getSession();
    if (!session) return;               // still on the auth gate; the event below retries
    booted = true;

    if (!session.admin) {
      var el = root();
      var locked = document.getElementById('taxHubLocked');
      if (el) el.hidden = true;
      if (locked) locked.hidden = false;
      return;
    }

    try { state.periodKey = localStorage.getItem(PREFS_PERIOD) || state.periodKey; } catch (e) {}
    if (state.periodKey === 'custom') state.periodKey = 'last_month';
    var r = rangeFor(state.periodKey) || rangeFor('last_month');
    state.from = r.from; state.to = r.to;
    state.checked = loadChecked('');
    load();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('rep-portal:authenticated', boot);

  // Exposed for console poking and for future Tax Hub screens that want the
  // same period maths without re-deriving it.
  /* CUSTOM_TAXFILING_BY_CLAUDE - the filing card's "Open numbers": show one state for one exact period
     (a custom range) and scroll the report into view. */
  function show(code, from, to) {
    state.periodKey = 'custom';
    state.from = from; state.to = to;
    state.stateCode = code;
    state.checked = loadChecked(code);
    load();
    var el = root();
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  window.TaxHub = { state: state, rangeFor: rangeFor, reload: load, render: render, show: show, LOGIN_PAGE: LOGIN_PAGE };
})();
